import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Shield,
  ShieldCheck,
  Crown,
  UserCheck,
  Mail,
  Calendar,
  Key,
  Settings,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { formatDate, getInitials } from '../lib/utils';
import { usePaginatedAdminUsers } from '../hooks/usePaginatedAdminUsers';
import { AddAdminUserModal } from '../components/modals/AddAdminUserModal';
import { UserProfileModal } from '../components/UserProfileModal';
import { EditUserModal } from '../components/EditUserModal';
import { updateAdminUser } from '../services/adminUserService';
import { User } from '../types';

const userRoles = ['All', 'super_admin', 'manager', 'support', 'viewer'];

export function Users() {
  const [showFilters, setShowFilters] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  // Use paginated admin users hook
  const {
    users,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedRole,
    isSearchMode,
    isFilterMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    setSelectedRole,
    handleSearch,
    fetchPage,
    refresh,
    updateUserInCache,
    removeUserFromCache,
    invalidateCache,
  } = usePaginatedAdminUsers({ pageSize: 20 });

  const getRoleConfig = (role: string) => {
    switch (role) {
      case 'super_admin':
        return { 
          color: 'bg-indigo-100 text-indigo-800', 
          icon: Crown,
          label: 'Super Admin',
          description: 'Full system access'
        };
      case 'manager':
        return { 
          color: 'bg-purple-100 text-purple-800', 
          icon: ShieldCheck,
          label: 'Manager',
          description: 'Business operations'
        };
      case 'support':
        return { 
          color: 'bg-blue-100 text-blue-800', 
          icon: UserCheck,
          label: 'Support',
          description: 'Customer service'
        };
      case 'viewer':
        return { 
          color: 'bg-gray-100 text-gray-800', 
          icon: Eye,
          label: 'Viewer',
          description: 'Read-only access'
        };
      default:
        return { 
          color: 'bg-gray-100 text-gray-800', 
          icon: Shield,
          label: 'Unknown',
          description: 'Unknown role'
        };
    }
  };

  const getStatusConfig = (isActive: boolean) => {
    return isActive
      ? {
          color: 'bg-green-100 text-green-800',
          label: 'Active'
        }
      : {
          color: 'bg-red-100 text-red-800',
          label: 'Inactive'
        };
  };

  const getLastLoginStatus = (lastLogin: Date | undefined) => {
    if (!lastLogin) {
      return { color: 'text-gray-600', label: 'Never logged in' };
    }

    const now = new Date();
    const diffHours = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return { color: 'text-green-600', label: 'Recently active' };
    } else if (diffHours < 168) { // 7 days
      return { color: 'text-yellow-600', label: 'Active this week' };
    } else {
      return { color: 'text-red-600', label: 'Inactive' };
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await refresh();
      toast.success('Admin users refreshed successfully');
    } catch (error) {
      console.error('Error refreshing admin users:', error);
      toast.error('Failed to refresh admin users. Please try again.');
    }
  };

  // Handle view user profile
  const handleViewProfile = (user: User) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    setUserToEdit(user);
    setShowEditModal(true);
  };

  // Handle save user (shared by both modals)
  const handleSaveUser = async (updatedUser: User) => {
    try {
      await updateAdminUser(updatedUser.id, {
        name: updatedUser.name,
        email: updatedUser.email,
        role: updatedUser.role,
        isActive: updatedUser.isActive
      });
      toast.success('User updated successfully');
      
      if (isSearchMode || isFilterMode || isCombinedMode) {
        // Use cache updates when in any special mode
        updateUserInCache(updatedUser);
      } else {
        // For browse mode, do a full refresh
        await refresh();
      }
      
      // Close whichever modal is open
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user. Please try again.');
      throw error; // Re-throw to let the modal handle the error state
    }
  };

  // Handle user update from profile modal
  const handleUserUpdate = async (updatedUser: User) => {
    await handleSaveUser(updatedUser);
    // Update the selected user for the profile modal
    setSelectedUser(updatedUser);
  };

  // Show error toasts when errors occur
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Handle page change with auto-scroll
  const handlePageChange = (page: number) => {
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Auto-scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Handle successful user creation
  const handleAddUserSuccess = async () => {
    await refresh();
    toast.success('Admin users table refreshed');
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-between items-center"
      >
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
        </div>
        <Button
          onClick={() => setShowAddUserModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Admin User
        </Button>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-jamaica-green/20"
              />
              {(searchLoading || filterLoading) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              {selectedRole !== 'All' && (
                <Badge className={`ml-1 border-transparent ${showFilters ? 'bg-white text-jamaica-green hover:bg-white hover:text-jamaica-green' : 'bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white'}`}>
                  1
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 pt-4 border-t border-gray-200 space-y-4"
          >
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700 mr-2">Role:</span>
              {userRoles.map((role) => (
                <Button
                  key={role}
                  variant={selectedRole === role ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedRole(role)}
                  className={selectedRole === role ? "bg-jamaica-green hover:bg-jamaica-green/90" : ""}
                >
                  {role === 'All' ? 'All' : getRoleConfig(role).label}
                </Button>
              ))}
            </div>
            
            {/* Clear Filters */}
            {selectedRole !== 'All' && (
              <div className="pt-2 border-t border-gray-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedRole('All')}
                  className="text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10"
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-jamaica-green" />
              Admin Users ({totalCount})
              {isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-black border-jamaica-black hover:bg-transparent hover:text-jamaica-black hover:border-jamaica-black">
                  Combined Mode
                </Badge>
              )}
              {isSearchMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-green border-jamaica-green hover:bg-transparent hover:text-jamaica-green hover:border-jamaica-green">
                  Search Mode
                </Badge>
              )}
              {isFilterMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-yellow border-jamaica-yellow hover:bg-transparent hover:text-jamaica-yellow hover:border-jamaica-yellow">
                  Filter Mode
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Admin Users</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : loading || searchLoading || filterLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                <span className="ml-2 text-gray-600">
                  {searchLoading ? 'Searching admin users...' : 
                   filterLoading ? 'Filtering admin users...' : 
                   'Loading admin users...'}
                </span>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Shield className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Admin Users Found</h3>
                <p className="text-gray-600 mb-4">
                  {isCombinedMode 
                    ? 'No admin users match your search query and filters.'
                    : isSearchMode 
                    ? 'No admin users match your search criteria.'
                    : isFilterMode
                    ? 'No admin users match the selected filters.'
                    : 'No admin users have been registered yet.'}
                </p>
                {(searchQuery || isFilterMode) && (
                  <div className="space-y-2">
                    {searchQuery && (
                      <Button onClick={() => handleSearch('')} variant="outline">
                        Clear Search
                      </Button>
                    )}
                    {isFilterMode && (
                      <Button onClick={() => setSelectedRole('All')} variant="outline">
                        Clear Filters
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>User</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user, index) => {
                        const roleConfig = getRoleConfig(user.role);
                        const statusConfig = getStatusConfig(user.isActive);
                        const RoleIcon = roleConfig.icon;
                        
                        return (
                          <motion.tr
                            key={user.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full flex items-center justify-center text-white font-medium">
                                  {getInitials(user.name)}
                                </div>
                                <div className="space-y-1">
                                  <p className="font-medium text-gray-900">{user.name}</p>
                                  <p className="text-sm text-gray-500">ID: {user.id}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Mail className="w-3 h-3 text-gray-400" />
                                <p className="text-sm text-gray-600">{user.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Badge className={roleConfig.color}>
                                  <RoleIcon className="w-3 h-3 mr-1" />
                                  {roleConfig.label}
                                </Badge>
                                <p className="text-xs text-gray-500">{roleConfig.description}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewProfile(user)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Profile
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit User
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </motion.tr>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {!isSearchMode && !isFilterMode && !isCombinedMode && (
                  <div className="p-6 border-t border-gray-200">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      loading={loading}
                      totalItems={totalCount}
                      itemsPerPage={20}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Add Admin User Modal */}
      <AddAdminUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSuccess={handleAddUserSuccess}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        user={selectedUser}
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onUserUpdate={handleUserUpdate}
      />

      {/* Edit User Modal */}
      <EditUserModal
        user={userToEdit}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveUser}
      />
    </div>
  );
} 